/**
 * PATH: packages/ui/src/components/notification-center/types.ts
 * FILE_ID: UI-NOTIFCENTER-TYPES-001
 * PURPOSE: Notification types, severity styles, and date grouping.
 * WHY: The default info style was a blue wash, so ordinary rows looked like a blue panel.
 */

import {
  AlertTriangle,
  Info,
  CheckCircle2,
  Briefcase,
  FileText,
  ClipboardCheck,
  Mail,
  Bell,
} from 'lucide-react'

export interface NotificationItem {
  id: string | number
  unique_key?: string
  category: 'financial' | 'operational' | 'deadlines' | 'mentions' | 'activity' | 'system' | string
  severity: 'critical' | 'warning' | 'info' | 'success'
  title: string
  message: string
  action_url?: string | null
  action_label?: string | null
  id_type?: string | null
  pagename?: string | null
  is_read?: boolean
  read_at?: string | null
  resolved_at?: string | null
  expires_at?: string | null
  created_at: string
  updated_at?: string
  sender_name?: string | null
  entity_link?: string | null
  time_ago?: string
  job_number?: string | number | null
  metadata?: Record<string, any>
  /** @deprecated use category instead */
  type?: 'alert' | 'mention' | 'update' | 'info'
  /** @deprecated use sender_name instead */
  by_user?: { id: string; name: string; avatar?: string }
}

export interface NotificationCenterProps {
  notifications?: NotificationItem[]
  unreadCount?: number
  isLoading?: boolean
  isOpen?: boolean
  onClose?: () => void
  apiEndpoint?: string
  onMarkAsRead?: (id: string) => void
  onMarkAllAsRead?: () => void
  onDismiss?: (id: string) => void
  onDismissAll?: () => void
  onRefresh?: () => void
  onOpenSettings?: () => void
  apiClient?: {
    post: (url: string, data: any) => Promise<any>
    get: (url: string) => Promise<any>
    delete: (url: string) => Promise<any>
  }
  /** SPA navigation callback — avoids full page reload when clicking a notification */
  onNavigate?: (url: string) => void
  /** Hide the trigger button - for use when trigger is in Header */
  hideTrigger?: boolean
}

export type FilterType = 'all' | 'unread' | 'alerts' | 'mentions' | 'focus' | 'timesheets' | 'qa' | 'updates'

const VERSION_NOISE = /version available|is live|refresh to update/i
const TIMESHEET = /timesheet/i
const QA_ITEM = /qa check|inspection|checklist/i

export function isVersionUpdate(notification: NotificationItem): boolean {
  const text = `${notification.title || ''} ${notification.message || ''}`
  return notification.category === 'system' || VERSION_NOISE.test(text)
}

export function isTimesheetNotification(notification: NotificationItem): boolean {
  return TIMESHEET.test(`${notification.title || ''} ${notification.message || ''}`)
}

export function isQaNotification(notification: NotificationItem): boolean {
  const text = `${notification.title || ''} ${notification.message || ''} ${notification.id_type || ''} ${notification.pagename || ''}`
  return QA_ITEM.test(text)
}

/** Unread work that asks for a decision. Product-update banners stay out. */
export function isFocusNotification(notification: NotificationItem): boolean {
  if (isVersionUpdate(notification)) return false
  const unread = !notification.read_at && !notification.is_read
  if (!unread) return false
  return (
    isTimesheetNotification(notification) ||
    isQaNotification(notification) ||
    notification.category === 'mentions' ||
    notification.severity === 'critical' ||
    notification.severity === 'warning' ||
    Boolean(notification.action_label)
  )
}

export interface SeverityStyle {
  icon: any
  bgColor: string
  textColor: string
  borderColor: string
  dotColor: string
}

export const getSeverityStyles = (severity: string): SeverityStyle => {
  switch (severity) {
    case 'critical':
      return {
        icon: AlertTriangle,
        bgColor: 'bg-red-50 dark:bg-red-900/20',
        textColor: 'text-red-600 dark:text-red-400',
        borderColor: 'border-red-200 dark:border-red-800',
        dotColor: 'bg-red-500',
      }
    case 'warning':
      return {
        icon: AlertTriangle,
        bgColor: 'bg-amber-50 dark:bg-amber-900/20',
        textColor: 'text-amber-600 dark:text-amber-400',
        borderColor: 'border-amber-200 dark:border-amber-800',
        dotColor: 'bg-amber-500',
      }
    case 'success':
      return {
        icon: CheckCircle2,
        bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
        textColor: 'text-emerald-600 dark:text-emerald-400',
        borderColor: 'border-emerald-200 dark:border-emerald-800',
        dotColor: 'bg-emerald-500',
      }
    default:
      return {
        icon: Info,
        bgColor: 'bg-primary/10',
        textColor: 'text-primary',
        borderColor: 'border-primary/30',
        dotColor: 'bg-primary',
      }
  }
}

export const getEntityIcon = (entityType: string | undefined) => {
  switch (entityType) {
    case 'job': return Briefcase
    case 'quote': return FileText
    case 'inspection':
    case 'ncr': return ClipboardCheck
    case 'email': return Mail
    default: return Bell
  }
}

export interface NotificationGroup {
  label: string
  items: NotificationItem[]
}

export const groupByDate = (notifications: NotificationItem[]): NotificationGroup[] => {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  const weekAgo = new Date(today)
  weekAgo.setDate(weekAgo.getDate() - 7)

  const buckets: Record<string, NotificationItem[]> = {}
  const order: string[] = []

  const addToBucket = (key: string, n: NotificationItem) => {
    if (!buckets[key]) { buckets[key] = []; order.push(key) }
    buckets[key].push(n)
  }

  notifications.forEach((n) => {
    const date = new Date(n.created_at)
    if (date >= today) addToBucket('Today', n)
    else if (date >= yesterday) addToBucket('Yesterday', n)
    else if (date >= weekAgo) addToBucket('This Week', n)
    else {
      const monthLabel = date.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })
      addToBucket(monthLabel, n)
    }
  })

  return order.map((label) => ({ label, items: buckets[label] }))
}

const ALERT_MESSAGE_PATTERNS = /wip|budget|overdue|exceed|hours.*budget/i

export const filterNotifications = (
  notifications: NotificationItem[],
  filter: FilterType
): NotificationItem[] => {
  switch (filter) {
    case 'unread':
      return notifications.filter((n) => !n.read_at && !n.is_read)
    case 'alerts':
      return notifications.filter(
        (n) =>
          ['financial', 'deadlines', 'operational'].includes(n.category) ||
          n.severity === 'critical' ||
          n.severity === 'warning' ||
          ALERT_MESSAGE_PATTERNS.test(n.message || '') ||
          ALERT_MESSAGE_PATTERNS.test(n.title || '')
      )
    case 'mentions':
      return notifications.filter(
        (n) => n.category === 'mentions' || (n.message || '').includes('@')
      )
    case 'focus':
      return notifications.filter(isFocusNotification)
    case 'timesheets':
      return notifications.filter(isTimesheetNotification)
    case 'qa':
      return notifications.filter(isQaNotification)
    case 'updates':
      return notifications.filter(isVersionUpdate)
    default:
      return notifications
  }
}
